// Copyright ©️ 2026 eVolve MEP, LLC

import type { JwtPayload } from 'jwt-decode';

import type { StrongExclude, StrongExtract } from '../_helpers/helper-types';

import type { ProductId } from '../Management/types';
import type { UserName } from '../User/types';

import type { DateOnly, DateRange } from './dateOnly';

declare const companyIdSymbol: unique symbol;
export type CompanyId = string & { [companyIdSymbol]: never };

export type Company = {
  companyId: CompanyId;
  companyName: string;
  status: unknown;
  creationDate: string;
  netsuiteId?: string;
  optInToCustomRolePermissions: boolean | null;
  externalIdpName?: string;
};

declare const hexTripletSymbol: unique symbol;
export type HexTriplet = string & { [hexTripletSymbol]: never };
export const isHexTriplet = (maybeHexTriplet: string): maybeHexTriplet is HexTriplet =>
  /^#[0-9A-Fa-f]{6}$/i.test(maybeHexTriplet);
export const FLAT_BLACK_FILLER = '#000000' as HexTriplet;
export const FLAT_WHITE_FILLER = '#FFFFFF' as HexTriplet;

export const timeFrameOptions = [
  'This Year',
  'This Month',
  'This Week',
  'Last Year',
  'Last Month',
  'Last Week',
  'Specific Date',
  'Date Range',
  'All Time',
] as const;
export type TimeFrameOption = (typeof timeFrameOptions)[number];

export type TimeFrameParams = {
  timeFrame?: TimeFrameOption | null;
  beginDate?: DateOnly | null;
  endDate?: DateOnly | null;
  specificDate?: DateOnly | null;
  currentDate?: DateOnly | null;
};

export type TimeFrameData =
  | {
      timeFrame: StrongExclude<TimeFrameOption, 'Date Range' | 'Specific Date'> | null;
      date: null;
      dateRange: null;
    }
  | {
      timeFrame: StrongExtract<TimeFrameOption, 'Specific Date'>;
      date: DateOnly | null;
      dateRange: null;
    }
  | {
      timeFrame: StrongExtract<TimeFrameOption, 'Date Range'>;
      dateRange: DateRange;
      date: null;
    };

type RoleScope = 'Company' | 'Evolve' | 'Shop';
type RoleName = 'Administrator' | 'MarketingManager' | 'ShopFloor' | 'FullAccess' | 'LimitedAccess';

export const ShopRoles = [
  'Evolve.MarketingManager',
  'Company.Administrator',
  'Shop.FullAccess',
  'Shop.ShopFloor',
  'Shop.LimitedAccess',
] as const satisfies `${RoleScope}.${RoleName}`[];

export type ShopRole = (typeof ShopRoles)[number];

export type EvolveJwtToken = JwtPayload & {
  roles: ShopRole[];
  licenses: ProductId[];
  username: UserName;
};
